import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Wallet, Copy, Check, Shield, Activity } from 'lucide-react';
import { useWallet } from '../contexts/WalletContext';
import { useIsCallerAdmin, useIsCallerTemporaryAdmin } from '../hooks/useQueries';
import WalletConnectionModal from './WalletConnectionModal';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export default function Header() {
  const { isConnected, walletAddress, walletType, disconnect: disconnectWallet } = useWallet();
  const { data: isAdmin } = useIsCallerAdmin();
  const { data: isTemporaryAdmin } = useIsCallerTemporaryAdmin();
  const [modalOpen, setModalOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Show admin dashboard if either wallet-based admin OR temporary admin session is active
  const showAdminDashboard = isAdmin || isTemporaryAdmin;

  const handleDisconnectWallet = () => {
    disconnectWallet();
    toast.success('Wallet disconnected');
  };

  const handleCopyAddress = async () => {
    if (walletAddress) {
      await navigator.clipboard.writeText(walletAddress);
      setCopied(true);
      toast.success('Address copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleAdminDashboard = () => {
    const adminSection = document.getElementById('admin-dashboard-section');
    if (adminSection) {
      adminSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleValidationDashboard = () => {
    const validationSection = document.getElementById('validation-dashboard-section');
    if (validationSection) {
      validationSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleRavencoinOrderBook = () => {
    const ravencoinSection = document.getElementById('ravencoin-orderbook-section');
    if (ravencoinSection) {
      ravencoinSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleDiscord = () => {
    // KawSwap Discord Server - Server ID: 1460231619339227230
    // Permanent invite link: https://discord.gg/T6HSb8eZ8G
    const discordInviteLink = 'https://discord.gg/T6HSb8eZ8G';
    window.open(discordInviteLink, '_blank', 'noopener,noreferrer');
  };

  const handleTwitter = () => {
    // KawSwap X/Twitter profile: https://x.com/kawswap
    const twitterLink = 'https://x.com/kawswap';
    window.open(twitterLink, '_blank', 'noopener,noreferrer');
  };

  const truncateAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const getWalletTypeName = () => {
    switch (walletType) {
      case 'web3':
        return 'Web3 Wallet';
      case 'ravencoin':
        return 'Ravencoin Wallet';
      default:
        return 'Wallet';
    }
  };

  const getWalletIcon = () => {
    switch (walletType) {
      case 'web3':
        return '🦊';
      case 'ravencoin':
        return '🐦';
      default:
        return '';
    }
  };

  // Discord icon component (Discord brand icon - "Clyde")
  const DiscordIcon = ({ className }: { className?: string }) => (
    <svg
      viewBox="0 0 71 55"
      fill="currentColor"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1066 30.1693C30.1066 34.1136 27.28 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7636 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.6986 30.1693C53.6986 34.1136 50.9 37.3253 47.3178 37.3253Z" />
    </svg>
  );

  // X/Twitter icon component (X logo)
  const XIcon = ({ className }: { className?: string }) => (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );

  return (
    <>
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-primary via-purple-400 to-primary bg-clip-text text-transparent">
                KawSwap
              </h1>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                onClick={handleDiscord}
                className="p-2 hover:bg-primary/10 hover:text-primary transition-colors"
                aria-label="Join Discord Community"
                title="Join Discord Community"
              >
                <DiscordIcon className="h-5 w-5" />
              </Button>
              <Button
                variant="ghost"
                onClick={handleTwitter}
                className="p-2 hover:bg-primary/10 hover:text-primary transition-colors"
                aria-label="Follow on X"
                title="Follow on X"
              >
                <XIcon className="h-5 w-5" />
              </Button>
              
              {showAdminDashboard && (
                <>
                  <Button
                    variant="outline"
                    onClick={handleAdminDashboard}
                    className="gap-2 border-primary/50 hover:bg-primary/10"
                  >
                    <Shield className="h-4 w-4 text-primary" />
                    <span className="hidden sm:inline">Admin</span>
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleValidationDashboard}
                    className="gap-2 border-green-500/50 hover:bg-green-500/10"
                  >
                    <Activity className="h-4 w-4 text-green-500" />
                    <span className="hidden sm:inline">Validation</span>
                  </Button>
                </>
              )}
              
              {isConnected ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="secondary" className="gap-2">
                      <span className="text-lg">{getWalletIcon()}</span>
                      <Wallet className="h-4 w-4" />
                      <span className="hidden sm:inline">{truncateAddress(walletAddress!)}</span>
                      <span className="sm:hidden">{walletAddress!.slice(0, 6)}...</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64">
                    <DropdownMenuLabel>
                      <div className="flex flex-col space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{getWalletIcon()}</span>
                          <p className="text-sm font-medium">{getWalletTypeName()}</p>
                        </div>
                        <p className="text-xs text-muted-foreground font-mono">
                          {truncateAddress(walletAddress!)}
                        </p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleDiscord} className="cursor-pointer">
                      <DiscordIcon className="mr-2 h-4 w-4" />
                      <span>Discord Community</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleTwitter} className="cursor-pointer">
                      <XIcon className="mr-2 h-4 w-4" />
                      <span>Follow on X</span>
                    </DropdownMenuItem>
                    {showAdminDashboard && (
                      <>
                        <DropdownMenuSeparator className="sm:hidden" />
                        <DropdownMenuItem onClick={handleRavencoinOrderBook} className="cursor-pointer">
                          <span className="mr-2">🐦</span>
                          <span>Ravencoin Order Book</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={handleAdminDashboard} className="cursor-pointer">
                          <Shield className="mr-2 h-4 w-4 text-primary" />
                          <span>Admin Dashboard</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={handleValidationDashboard} className="cursor-pointer">
                          <Activity className="mr-2 h-4 w-4 text-green-500" />
                          <span>Validation Dashboard</span>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem onClick={handleCopyAddress} className="cursor-pointer">
                      {copied ? (
                        <Check className="mr-2 h-4 w-4" />
                      ) : (
                        <Copy className="mr-2 h-4 w-4" />
                      )}
                      <span>{copied ? 'Copied!' : 'Copy Address'}</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleDisconnectWallet} className="cursor-pointer">
                      <Wallet className="mr-2 h-4 w-4" />
                      <span>Disconnect Wallet</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button 
                  onClick={() => setModalOpen(true)}
                  className="gap-2"
                >
                  <Wallet className="h-4 w-4" />
                  Connect Wallet
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <WalletConnectionModal open={modalOpen} onOpenChange={setModalOpen} />
    </>
  );
}
